import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Phone, Plus, Clock, User, CalendarIcon, CheckCircle, XCircle, AlertCircle, Search, Filter, PhoneCall, AlertTriangle, Lock, Bell } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-800', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  missed: { label: 'Missed', color: 'bg-red-100 text-red-800', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-800', icon: PhoneCall }
};

const priorityConfig = {
  urgent: { label: 'Urgent', color: 'bg-red-500' },
  high: { label: 'High', color: 'bg-orange-500' },
  normal: { label: 'Normal', color: 'bg-blue-500' },
  low: { label: 'Low', color: 'bg-gray-400' }
};

export default function CallCenterCallbacks() {
  const [callbacks, setCallbacks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('scheduled');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    contact_id: '',
    contact_name: '',
    phone_number: '',
    scheduled_time: new Date(),
    reason: '',
    notes: '',
    priority: 'normal'
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const [callbacksRes, contactsRes] = await Promise.all([
        axios.get(`${API}/call-center/callbacks?${params}`, { headers }),
        axios.get(`${API}/contacts?page_size=100`, { headers })
      ]);
      
      setCallbacks(callbacksRes.data.callbacks || []);
      setContacts(contactsRes.data.contacts || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load callbacks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createCallback = async () => {
    if (!formData.phone_number || !formData.scheduled_time) {
      toast.error('Phone number and scheduled time are required');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/call-center/callbacks`, {
        ...formData,
        scheduled_time: formData.scheduled_time.toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Callback scheduled successfully');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to create callback:', err);
      toast.error('Failed to schedule callback');
    }
  };

  const updateCallbackStatus = async (callbackId, newStatus) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/callbacks/${callbackId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Callback marked as ${newStatus}`);
      fetchData();
    } catch (err) {
      console.error('Failed to update callback:', err);
      toast.error('Failed to update callback');
    }
  };

  const resetForm = () => {
    setFormData({
      contact_id: '',
      contact_name: '',
      phone_number: '',
      scheduled_time: new Date(),
      reason: '',
      notes: '',
      priority: 'normal'
    });
  };

  const selectContact = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      setFormData({
        ...formData,
        contact_id: contact.id,
        contact_name: `${contact.first_name} ${contact.last_name}`,
        phone_number: contact.phone || contact.mobile || ''
      });
    }
  };

  const filteredCallbacks = callbacks.filter(cb => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!cb.contact_name?.toLowerCase().includes(search) &&
          !cb.phone_number?.includes(search)) {
        return false;
      }
    }
    return true;
  });

  // Group by date - handle both scheduled_time and scheduled_at field names
  const groupedCallbacks = filteredCallbacks.reduce((acc, cb) => {
    const scheduleDate = cb.scheduled_time || cb.scheduled_at;
    if (!scheduleDate) return acc;
    const date = new Date(scheduleDate).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(cb);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: callbacks.length,
    today: callbacks.filter(cb => {
      const scheduleDate = cb.scheduled_time || cb.scheduled_at;
      if (!scheduleDate) return false;
      const cbDate = new Date(scheduleDate).toDateString();
      return cbDate === new Date().toDateString();
    }).length,
    overdue: callbacks.filter(cb => {
      const scheduleDate = cb.scheduled_time || cb.scheduled_at;
      if (!scheduleDate) return false;
      return cb.status === 'scheduled' && new Date(scheduleDate) < new Date();
    }).length,
    completed: callbacks.filter(cb => cb.status === 'completed').length
  };

  return (
    <Layout>
    <div className="p-6 space-y-6">
      {/* Phase 2 Preview Banner */}
      <Alert className="bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
        <Bell className="h-5 w-5 text-orange-600" />
        <AlertDescription className="ml-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-semibold text-orange-800 dark:text-orange-300">Phase 2 Prep Mode</span>
              <span className="text-orange-700 dark:text-orange-400 ml-2">
                Schedule and manage callbacks. Automated reminders and Dialer integration will activate in Active Mode.
              </span>
            </div>
            <Badge variant="outline" className="border-orange-500 text-orange-700 dark:text-orange-300">
              Ready for Phase 2
            </Badge>
          </div>
        </AlertDescription>
      </Alert>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Callbacks</h1>
          <p className="text-muted-foreground">Schedule and manage customer callbacks</p>
        </div>
        <Button onClick={() => setShowDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Schedule Callback
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Scheduled</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Phone className="h-8 w-8 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Due Today</p>
                <p className="text-2xl font-bold">{stats.today}</p>
              </div>
              <CalendarIcon className="h-8 w-8 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Callbacks List */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : Object.keys(groupedCallbacks).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Phone className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold">No Callbacks Found</h3>
            <p className="text-muted-foreground mb-4">Schedule a callback to get started</p>
            <Button onClick={() => setShowDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Schedule Callback
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedCallbacks).sort(([a], [b]) => new Date(a) - new Date(b)).map(([date, items]) => (
            <div key={date}>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                {date === new Date().toLocaleDateString() ? 'Today' : date}
              </h3>
              <div className="space-y-2">
                {items.sort((a, b) => new Date(a.scheduled_time || a.scheduled_at) - new Date(b.scheduled_time || b.scheduled_at)).map(callback => {
                  const status = statusConfig[callback.status] || statusConfig.scheduled;
                  const StatusIcon = status.icon;
                  const priority = priorityConfig[callback.priority] || priorityConfig.normal;
                  const scheduleDate = callback.scheduled_time || callback.scheduled_at;
                  const isOverdue = callback.status === 'scheduled' && scheduleDate && new Date(scheduleDate) < new Date();
                  
                  return (
                    <Card key={callback.id} className={isOverdue ? 'border-red-300' : ''}>
                      <CardContent className="py-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-1 h-12 rounded-full ${priority.color}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{callback.contact_name || 'Unknown'}</span>
                              <Badge className={status.color}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {status.label}
                              </Badge>
                              {isOverdue && (
                                <Badge variant="destructive">Overdue</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {callback.phone_number}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {scheduleDate ? format(new Date(scheduleDate), 'h:mm a') : '-'}
                              </span>
                            </div>
                            {callback.reason && (
                              <p className="text-sm mt-1">{callback.reason}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {callback.status === 'scheduled' && (
                              <>
                                <Button size="sm" onClick={() => updateCallbackStatus(callback.id, 'completed')}>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Complete
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => updateCallbackStatus(callback.id, 'missed')}>
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Missed
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Callback Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Callback</DialogTitle>
            <DialogDescription>Schedule a callback with a customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Contact (Optional)</Label>
              <Select onValueChange={selectContact}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map(contact => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name} - {contact.phone || contact.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Customer name"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date & Time *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.scheduled_time, 'PPP p')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.scheduled_time}
                      onSelect={(date) => date && setFormData({ ...formData, scheduled_time: date })}
                    />
                    <div className="p-3 border-t">
                      <Input
                        type="time"
                        value={format(formData.scheduled_time, 'HH:mm')}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(':');
                          const newDate = new Date(formData.scheduled_time);
                          newDate.setHours(parseInt(hours), parseInt(minutes));
                          setFormData({ ...formData, scheduled_time: newDate });
                        }}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Reason for Callback</Label>
              <Input
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="e.g., Follow up on quote"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={createCallback}>Schedule Callback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
